import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Message, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import * as sanitizeHtml from 'sanitize-html';
import { ToastSeverity, toastSignal } from './toasts.signals';

@Component({
  selector: 'kon-toasts',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ButtonModule],
  template: `<p-toast [autoZIndex]="true">
    <ng-template let-message pTemplate="message">
      <span [class]="'p-toast-message-icon ' + toastIcon(message)"></span>
      <div class="p-toast-message-text">
        <div class="p-toast-summary">
          {{ message.summary }}
        </div>
        <div
          class="p-toast-detail"
          [innerHTML]="toastDetail(message.detail)"
        ></div>
        <div class="p-toast-buttons pt-2 flex flex-row justify-end">
          <p-button
            *ngFor="let action of message.actions"
            [label]="action.label"
            size="small"
            [outlined]="true"
            [severity]="buttonSeverity(message)"
            (click)="action.command(message)"
          ></p-button>
        </div>
      </div>
    </ng-template>
  </p-toast>`,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent {
  private messageService = inject(MessageService);

  constructor() {
    effect(() => {
      const message = toastSignal();
      if (message) {
        this.messageService.add({
          ...message,
          severity: this.toastSeverity(message),
        });
      }
    });
  }

  toastIcon(message?: Message) {
    if (message?.icon) return message?.icon;
    switch (message?.severity as ToastSeverity) {
      case 'success':
        return 'pi pi-check-circle';
      case 'info':
        return 'pi pi-info-circle';
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'danger':
        return 'pi pi-times-circle';
      default:
        return 'pi pi-info-circle';
    }
  }

  buttonSeverity(message?: Message) {
    return message?.severity as ToastSeverity;
  }

  toastSeverity(message?: Message) {
    switch (message?.severity as ToastSeverity) {
      case 'warning':
        return 'warn';
      case 'danger':
        return 'error';
      default:
        return message?.severity;
    }
  }

  toastDetail(textOrHtml?: string): string {
    return sanitizeHtml(textOrHtml?.replace('\n', '<br/>') ?? '');
  }
}
